<template>
  <v-app-bar
    height="56"
    class="app-bar"
    data-testid="app-bar"
    :class="{
      'with-rail': !showMenuToggle && !isAssessmentMode,
      'gradient-background': shouldShowLogo
    }"
    :style="{
      marginLeft: (showMenuToggle || isAssessmentMode) ? '0px' : '56px',
      width: (showMenuToggle || isAssessmentMode) ? '100%' : 'calc(100% - 56px)'
    }"
  >
    <v-app-bar-nav-icon
      v-if="showMenuToggle"
      class="mr-1"
      icon="mdi-menu"
      color="on-selectable"
      data-testid="nav-menu-toggle-button"
      @click="$emit('toggle-menu')"
    />
    
    <v-spacer />
    
    <div class="title-container">
      <div v-if="shouldShowLogo" class="mr-3 app-icon d-flex align-center justify-center" style="width: 48px; height: 48px;">
        <img src="~/assets/images/INDA.svg" alt="INDA" class="inda-icon">
      </div>
      <div v-else class="mr-3 app-icon d-flex align-center justify-center" style="width: 24px; height: 24px;">
        <v-icon v-if="icon" :icon="icon" size="24" />
      </div>
      <v-app-bar-title class="text-h6 font-weight-medium app-bar-title">{{ displayTitle }}</v-app-bar-title>
    </div>
    
    <v-spacer />
    
    <!-- User authentication area - always render a button to prevent hydration mismatch -->
    <div class="app-bar-profile-button">
      <!-- Show user info when authenticated -->
      <v-btn
        v-if="hasUserData"
        variant="text"
        color="on-selectable"
        :disabled="isAssessmentMode"
        data-testid="nav-user-account-button"
        @click="navigateToAccount"
      >
        <v-icon class="mr-2">mdi-account-circle</v-icon>
        <span v-if="shouldShowFullName">{{ displayName }}</span>
        <span v-else>{{ initials }}</span>
      </v-btn>
      
      <!-- Show login button when not authenticated -->
      <v-btn
        v-else
        variant="flat"
        color="primary"
        data-testid="nav-login-button"
        to="/login"
      >
        <v-icon start>mdi-login</v-icon>
        Login
      </v-btn>
    </div>
  </v-app-bar>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useAuth } from '~/composables/useAuth';
import { useDisplay } from 'vuetify';
import { useRoute } from 'vue-router';

const props = defineProps<{
  title: string;
  icon: string;
  showMenuToggle?: boolean;
  isAssessmentMode?: boolean;
}>();

defineEmits<{
  'toggle-menu': [];
}>();

const auth = useAuth();
const display = useDisplay();
const route = useRoute();

// Define public pages that should always show logo and "Independent Assessor" text
const publicPages = ['/faqs', '/legal', '/contact', '/demo', '/', '/login'];

const userInfo = computed(() => auth.userData.value);
const hasUserData = computed(() => {
  // During SSR or before auth hydration, assume no user data
  if (!auth.isClientHydrated.value) {
    return false;
  }
  return userInfo.value?.firstname && userInfo.value?.lastname;
});

// Determine if current page is public (should show logo) or private (should show page icon)
const isPublicPage = computed(() => {
  // During SSR or before auth hydration, determine page type based on route path only
  if (!auth.isClientHydrated.value) {
    return publicPages.includes(route.path);
  }
  // After hydration, use full logic including authentication state
  return publicPages.includes(route.path) || (!auth.isAuthenticated() && route.path !== '/account');
});

// Determine whether to show logo (public pages) or page icon (private pages)
const shouldShowLogo = computed(() => {
  // Use consistent logic with isPublicPage
  return isPublicPage.value;
});

// Determine the display title - public pages always show "Independent Assessor"
const displayTitle = computed(() => {
  if (isPublicPage.value) {
    return 'Independent Assessor';
  }
  // For private pages, always show the page-specific title
  return props.title || '';
});
const displayName = computed(() => {
  if (!auth.isClientHydrated.value || !userInfo.value?.firstname || !userInfo.value?.lastname) {
    return '';
  }
  return `${userInfo.value.firstname} ${userInfo.value.lastname}`;
});
const initials = computed(() => {
  if (!auth.isClientHydrated.value || !userInfo.value?.firstname || !userInfo.value?.lastname) {
    return '';
  }
  const { firstname, lastname } = userInfo.value;
  return `${firstname[0]}${lastname[0]}`;
});
const shouldShowFullName = computed(() => !display.xs.value);

// Initialize auth state after component mount
onMounted(() => {
  auth.hydrate();
});

const navigateToAccount = () => {
  navigateTo('/account');
};
</script>

<style scoped>
/* AppBar styling */
.app-bar {
  position: fixed !important;
  z-index: 1000 !important;
}

/* Gradient background for non-authenticated users */
.app-bar.gradient-background {
  background: linear-gradient(90deg, rgb(var(--v-theme-surface-appbar)) 60%, rgb(var(--v-theme-success)) 100%) !important;
}

/* Ensure all text elements in gradient background use on-success color */
.app-bar.gradient-background .v-app-bar-title {
  color: rgb(var(--v-theme-primary)) !important;
}

/* Add padding when rail is showing to space icon properly */
.app-bar.with-rail {
  padding-left: 16px !important;
}

/* Center title container */
.title-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 1 auto;
  min-width: 0;
}

/* User button styling */
.v-btn {
  text-transform: none;
}

/* Account button positioning */
.app-bar-profile-button {
  margin-right: 4px !important;
  flex-shrink: 0 !important;
}

.app-bar-profile-button .v-btn {
  flex-shrink: 0 !important;
}

/* INDA icon styling */
.inda-icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
}
</style>

